import { useState } from 'react'
import { messageOf } from '../lib/errors'
import type { PageWarning } from './admin/WarningsFlag'
import {
  ACTIVITY_COPY,
  activityLabel,
  monthlyActivityCeiling,
  needsConnector,
  sourceLabel,
  type EarningRule,
  type StarEconomy,
} from '../lib/starEarning'

/**
 * #/admin/stars — what each activity earns. REC-47, migration 099.
 *
 * Presentational: the route loads, these render and hand a save back. Kept in
 * components/ and fed by props so the suite can render them — an effect does
 * not run under renderToString (Traps).
 *
 * ── PEOPLE AND ROLES' TREATMENT (David, 9 Oct) ──────────────────────────────
 *
 * It was a card per activity, each a small form with its own Save, under a
 * lead paragraph and a ceiling sentence — seven forms tall before the month's
 * totals. Now a table: one line per activity, what it pays, its cap, whether
 * it is on and this month's totals. Changing one opens it in the right-hand
 * tray (`RuleEditor`), the People and roles / store items surface. The
 * standing sentences went where they are read: the cap rule into the tray,
 * beside the field it is about; the ceiling and the connector into the
 * notices flag (`earningNotices`); the month into one line under the table.
 *
 * ── THE LIST IS THE DATABASE'S, THE WORDS ARE OURS ──────────────────────────
 *
 * Rows come from star_earning_rules(), which walks the hard-coded catalogue in
 * its own order. Nothing here iterates ACTIVITY_COPY to decide what to show —
 * only to find the words for a key it was given.
 *
 * ── THE CAP SENTENCE IS A CLAIM THE SUITE HOLDS THE SERVER TO ───────────────
 *
 * "A new cap reaches somebody who has already earned under this activity this
 * month from next month" is what 099's snapshot does (rule 7), and
 * stars_you_earn.sql D8 fails if the server stops doing it. A screen that
 * implied a raised cap applied at once would be rule 4's worst shape. It sits
 * in the tray of every MONTHLY activity, under the cap field.
 *
 * ── A CONNECTOR ROW SAYS IT HAS NO CONNECTOR ────────────────────────────────
 *
 * BIS Safety's rule can be switched on, and nothing sends it events yet. The
 * table says "not connected" beside the state, and the tray says it in full,
 * because "on" and "paying nobody" look identical otherwise.
 *
 * ── NO PER-PERSON NUMBERS ───────────────────────────────────────────────────
 *
 * Totals per activity only (D10/B22). "People at the cap" is a count, and it is
 * here because it is the one signal that says a cap is too low.
 */

type Save = (rule: { ruleKey: string; stars: number; monthlyCap: number | null; isEnabled: boolean }) => Promise<void>

export function EarningTable({
  rules,
  selectedKey,
  onSelect,
}: {
  rules: EarningRule[]
  selectedKey: string | null
  onSelect: (ruleKey: string) => void
}) {
  return (
    <div className="roles__tablewrap">
      <table className="admin__table people__table starsadmin__table">
        <thead>
          <tr>
            <th scope="col">Activity</th>
            <th scope="col" className="starsadmin__num">Stars</th>
            <th scope="col" className="starsadmin__num">Most a month</th>
            <th scope="col">State</th>
            <th scope="col">This month</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((r) => {
            const open = selectedKey === r.ruleKey
            const monthly = r.periodKind === 'monthly'
            const unconnected = needsConnector(r)
            return (
              <tr
                key={r.ruleKey}
                data-rule={r.ruleKey}
                className={'people__row' + (open ? ' is-selected' : '') + (r.isEnabled ? '' : ' is-suspended')}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('button, a, input, label')) return
                  onSelect(r.ruleKey)
                }}
              >
                <th scope="row" className="roles__person">
                  <span className="roles__who">
                    <button
                      type="button"
                      className="roles__open"
                      aria-haspopup="dialog"
                      aria-expanded={open}
                      onClick={() => onSelect(r.ruleKey)}
                    >
                      <span className="roles__name">{activityLabel(r.ruleKey)}</span>
                    </button>
                    <span className="roles__meta">
                      {sourceLabel(r.source)} · {monthly ? 'Monthly' : 'Once'}
                    </span>
                  </span>
                </th>
                <td className="starsadmin__num">★ {r.stars.toLocaleString()}</td>
                <td className="starsadmin__num">
                  {monthly && r.monthlyCap !== null ? `★ ${r.monthlyCap.toLocaleString()}` : <span className="starsadmin__none">—</span>}
                </td>
                <td>
                  {/* The access column's dot-and-word, so "on" reads the same
                      on both administration tables. On but unconnected is the
                      pending tone: switched on, paying nobody. */}
                  <span className={`statuspill statuspill--${!r.isEnabled ? 'muted' : unconnected ? 'pending' : 'ok'}`}>
                    {!r.isEnabled ? 'Off' : 'On'}
                  </span>
                  {unconnected && <span className="admin__substate roles__meta">Not connected</span>}
                </td>
                <td className="starsadmin__month">
                  {r.peopleThisMonth === 0 ? (
                    <span className="starsadmin__none">Nobody yet</span>
                  ) : (
                    <>
                      ★ {r.awardedThisMonth.toLocaleString()} to {r.peopleThisMonth.toLocaleString()}{' '}
                      {r.peopleThisMonth === 1 ? 'person' : 'people'}
                    </>
                  )}
                  {monthly && r.peopleAtCap > 0 && (
                    <span className="roles__meta">{r.peopleAtCap.toLocaleString()} at the cap</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** The tray's body for one activity: what it is, the three things you can set, and its totals. */
export function RuleEditor({ rule, onSave }: { rule: EarningRule; onSave: Save }) {
  const [stars, setStars] = useState(String(rule.stars))
  const [cap, setCap] = useState(rule.monthlyCap === null ? '' : String(rule.monthlyCap))
  const [enabled, setEnabled] = useState(rule.isEnabled)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const monthly = rule.periodKind === 'monthly'
  const starsN = wholeNumber(stars)
  const capN = monthly ? wholeNumber(cap) : null
  const invalid = starsN === null || (monthly && capN === null)
  const dirty =
    starsN !== rule.stars ||
    (monthly && capN !== rule.monthlyCap) ||
    enabled !== rule.isEnabled
  const copy = ACTIVITY_COPY[rule.ruleKey]
  const idBase = `earn-${rule.ruleKey.replace(/[^a-z0-9]/gi, '-')}`

  async function save() {
    if (invalid || starsN === null) return
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      await onSave({ ruleKey: rule.ruleKey, stars: starsN, monthlyCap: monthly ? capN : null, isEnabled: enabled })
      setSaved(true)
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="panel panel--tray starsadmin__editor" data-rule={rule.ruleKey}>
      <div className="tray__meta">
        <span className="tagpill"><span className="tagpill__of">Source</span>{sourceLabel(rule.source)}</span>
        <span className="tagpill"><span className="tagpill__of">Pays</span>{monthly ? 'Monthly' : 'Once'}</span>
      </div>
      {copy && <p className="panel__note">{copy.when}</p>}
      {needsConnector(rule) && (
        <p className="earnrules__note" role="note">
          Not connected yet. Nothing sends {sourceLabel(rule.source)} events to OneConetec, so this
          pays nobody until a connector is built — switching it on changes nothing today.
        </p>
      )}

      <section className="panel__section">
        <h3 className="panel__h3">What it earns</h3>
        <div className="earnrules__fields">
          <label className="stars__field" htmlFor={`${idBase}-stars`}>
            <span className="earnrules__label">{monthly ? 'Stars each time' : 'Stars'}</span>
            <input
              id={`${idBase}-stars`}
              type="number"
              inputMode="numeric"
              min={0}
              max={10000}
              step={1}
              value={stars}
              onChange={(e) => setStars(e.target.value)}
            />
          </label>
          {monthly && (
            <label className="stars__field" htmlFor={`${idBase}-cap`}>
              <span className="earnrules__label">Most per person per month</span>
              <input
                id={`${idBase}-cap`}
                type="number"
                inputMode="numeric"
                min={0}
                max={100000}
                step={1}
                value={cap}
                onChange={(e) => setCap(e.target.value)}
              />
            </label>
          )}
          <label className="earnrules__toggle" htmlFor={`${idBase}-on`}>
            <input
              id={`${idBase}-on`}
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>On</span>
          </label>
        </div>
        <p className="earnrules__meta">
          Stars earned here land in the same balance as stars from colleagues and spend in the
          store. Changes apply to the next award; nothing already earned changes.
          {monthly && (
            <>
              {' '}<strong>A new cap reaches somebody who has already earned under that activity this
              month from next month.</strong>
            </>
          )}
        </p>
        {invalid && (
          <p className="earnrules__warn" role="alert">
            {monthly ? 'Stars and the monthly cap' : 'Stars'} must be whole numbers, 0 or more.
          </p>
        )}
        {error && <p className="earnrules__warn" role="alert">{error}</p>}
        <div className="starsadmin__save">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={!dirty || invalid || busy}
            onClick={save}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          {saved && !dirty && <span className="earnrules__meta" role="status">Saved.</span>}
        </div>
      </section>

      <section className="panel__section">
        <h3 className="panel__h3">Totals</h3>
        <dl className="starsadmin__facts">
          <div>
            <dt>This month</dt>
            <dd>
              ★ {rule.awardedThisMonth.toLocaleString()} to {rule.peopleThisMonth.toLocaleString()}{' '}
              {rule.peopleThisMonth === 1 ? 'person' : 'people'}
              {monthly && <>, {rule.peopleAtCap.toLocaleString()} at the cap</>}
            </dd>
          </div>
          <div>
            <dt>All time</dt>
            <dd>
              ★ {rule.awardedAllTime.toLocaleString()}
              {rule.recoveredAllTime > 0 && <>, ★ {rule.recoveredAllTime.toLocaleString()} taken back</>}
            </dd>
          </div>
          {rule.updatedByName && rule.updatedAt && (
            <div>
              <dt>Last changed</dt>
              <dd>{rule.updatedByName}, {formatDay(rule.updatedAt)}</dd>
            </div>
          )}
        </dl>
      </section>
    </div>
  )
}

/**
 * The month, in one line under the table. It was a card of four large figures;
 * the four are still here, at the size of a sentence.
 */
export function EconomyStrip({ economy }: { economy: StarEconomy }) {
  return (
    <dl className="starsadmin__economy" aria-label="Stars this month">
      <div><dt>This month, given by colleagues</dt><dd>★ {economy.fromColleagues.toLocaleString()}</dd></div>
      <div><dt>Earned by activity</dt><dd>★ {economy.fromActivity.toLocaleString()}</dd></div>
      <div><dt>Welcome grants</dt><dd>★ {economy.fromWelcome.toLocaleString()}</dd></div>
      <div><dt>Taken back</dt><dd>★ {economy.recovered.toLocaleString()}</dd></div>
    </dl>
  )
}

/**
 * The earning tab's notices, for the flag. In order: what needs acting on,
 * then the standing facts.
 */
export function earningNotices(
  rules: EarningRule[],
  economy: StarEconomy | null,
  allowance: number | null,
): PageWarning[] {
  const out: PageWarning[] = []
  // The _safely wrappers catch a failed award so a star can never stop
  // somebody signing in or posting — and write a row instead. This is where
  // that row is SEEN; without it the catch would be a silent swallow (rule
  // 14). Absent at zero: nothing to explain. It tints the flag.
  if (economy && economy.failedAwards > 0) {
    out.push({
      id: 'failed',
      tone: 'warn',
      text: (
        <>
          {economy.failedAwards.toLocaleString()} {economy.failedAwards === 1 ? 'award' : 'awards'} failed
          this month and paid nothing. The activity itself went through; the reason is in the event
          log as <code>stars.award_failed</code>.
        </>
      ),
    })
  }
  for (const r of rules.filter((x) => x.isEnabled && needsConnector(x))) {
    out.push({
      id: `connector-${r.ruleKey}`,
      tone: 'info',
      text: (
        <>
          <strong>{activityLabel(r.ruleKey)}</strong> is on, and nothing sends{' '}
          {sourceLabel(r.source)} events yet — it pays nobody until a connector is built.
        </>
      ),
    })
  }
  const ceiling = monthlyActivityCeiling(rules)
  out.push({
    id: 'ceiling',
    tone: 'info',
    text: (
      <>
        At these rates the most one person can earn from activity in a month is{' '}
        <strong>★ {ceiling.toLocaleString()}</strong>
        {allowance !== null && (
          <> — against the ★ {allowance.toLocaleString()} each person can give colleagues</>
        )}
        .
      </>
    ),
  })
  return out
}

/** A whole number 0 or more, or null. "" is null, not 0 — clearing a box is not setting it to zero. */
export function wholeNumber(s: string): number | null {
  if (!/^\d+$/.test(s.trim())) return null
  const n = Number(s.trim())
  return Number.isSafeInteger(n) ? n : null
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}
